import type { DropResult } from '@hello-pangea/dnd';
import type {
  Folder,
  UserBookmarks,
  WorkEngagementBookmarks,
} from '@/lib/bookmark/types';
import { toast } from 'sonner';

interface HandleDragEndArgs {
  readonly type: 'folder' | 'bookmark';
  readonly result: DropResult;
  readonly workflows: UserBookmarks | WorkEngagementBookmarks | undefined;
  readonly setWorkflows: (
    workflows: UserBookmarks | WorkEngagementBookmarks
  ) => void;
  readonly save: (
    data: UserBookmarks | WorkEngagementBookmarks
  ) => Promise<unknown>;
}

export function reorder<T>(
  list: T[],
  startIndex: number,
  endIndex: number
): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

export const isVisibleWorkflowFolder = (folder: Folder): boolean =>
  folder.kind === 'workflow' && !!folder.pinned;

export async function handleDragEnd({
  type,
  result,
  workflows,
  setWorkflows,
  save,
}: HandleDragEndArgs): Promise<void> {
  if (!result.destination) return;
  if (result.destination.index === result.source.index) return;

  let updatedFolders: Folder[] = [];
  const folders = workflows?.folders ?? [];

  if (type === 'bookmark') {
    const folderId = result.destination.droppableId;
    const folderIndex = folders.findIndex((f) => f.client_id === folderId);
    if (folderIndex === -1) return;

    const folder = folders[folderIndex];
    const bookmarks = folder.child_bookmarks ?? [];
    const updatedBookmarks = [...bookmarks];
    const [moved] = updatedBookmarks.splice(result.source.index, 1);
    updatedBookmarks.splice(result.destination.index, 0, moved);

    const from = Math.min(result.source.index, result.destination.index);
    const to = Math.max(result.source.index, result.destination.index);

    for (let i = from; i <= to; i++) {
      updatedBookmarks[i] = {
        ...updatedBookmarks[i],
        order: i,
      };
    }

    updatedFolders = folders.map((f, i) =>
      i === folderIndex ? { ...f, child_bookmarks: updatedBookmarks } : f
    );
  } else {
    const visible = folders.filter(isVisibleWorkflowFolder);
    const rest = folders.filter((f) => !isVisibleWorkflowFolder(f));

    const reordered = [...visible];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    updatedFolders = [...reordered, ...rest].map((f, i) => ({
      ...f,
      order: i,
    }));
  }

  if (!workflows) return;

  const previousWorkflows = { ...workflows };

  setWorkflows({
    ...workflows,
    folders: updatedFolders,
  });

  try {
    await save({
      ...workflows,
      folders: updatedFolders,
    });
  } catch (error) {
    console.error(error);
    setWorkflows(previousWorkflows);
    toast.error('Misslyckades att spara ordningen.');
  }
}
